import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Home as HomeIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import TabsList from './tabs-list';
import { removeCookie } from '../../helpers/cookie';
import { setUser } from '../../global-state/root-slice';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}



const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export default function DrawerNavigation({ children }: { children: JSX.Element }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [confirmLogout, setConfirmLogout] = React.useState(false);

  const onLogout = () => {
    removeCookie('s-accessToken');
    removeCookie('s-userId');
    dispatch(setUser({user: null, authToken: null}));
    setConfirmLogout(false)

    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 1000);
  };
 

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
       <Dialog open={confirmLogout}>
        <DialogTitle>Are you sure you want to logout?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmLogout(false)} color="primary">Cancel</Button>
          <Button variant="contained" onClick={onLogout}>Logout</Button>
        </DialogActions>
      </Dialog>
      <CssBaseline />
      <AppBar position="fixed" open={open} style={{ backgroundColor: 'white' }}>
        <Toolbar>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" color={'primary'}>
            Royale Treasures Auction
          </Typography>
          <Typography variant="h6" color={'primary'} style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
            &nbsp;Staff
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <Link key={'home'} to={'/'} style={{ textDecoration: 'none', color: 'black' }}>
            <ListItem button key={'home'}>
              <ListItemIcon><HomeIcon/></ListItemIcon>
              <ListItemText primary={'Home'} />
            </ListItem>
          </Link>
          <ListItem button key={'logout'} onClick={() => setConfirmLogout(true)}>
            <ListItemIcon><LogoutIcon/></ListItemIcon>
            <ListItemText primary={'Logout'} />
          </ListItem>
        </List>
        <Divider />
        <TabsList/>
      </Drawer>
      <Main open={open} style={{ minHeight: '100vh' }}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}